import React, { useEffect, useMemo, useState } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { fetchTopicFeedbackTypes, recordTopicFeedback, removeTopicFeedback } from '../../lib/learningEvents';

const buttonStyles = {
  understood: 'bg-green-50 text-green-700 hover:bg-green-100 border-green-100',
  confusing: 'bg-red-50 text-red-700 hover:bg-red-100 border-red-100',
  ask_advisor: 'bg-amber-50 text-amber-700 hover:bg-amber-100 border-amber-100',
  ask_attorney: 'bg-slate-50 text-slate-700 hover:bg-slate-100 border-slate-100',
  saved: 'bg-blue-50 text-blue-700 hover:bg-blue-100 border-blue-100'
};

const activeButtonStyles = {
  understood: 'bg-green-100 text-green-800 border-green-200',
  confusing: 'bg-red-100 text-red-800 border-red-200',
  ask_advisor: 'bg-amber-100 text-amber-800 border-amber-200',
  ask_attorney: 'bg-slate-100 text-slate-800 border-slate-200',
  saved: 'bg-blue-100 text-blue-800 border-blue-200'
};

const feedbackActions = [
  {
    feedbackType: 'understood',
    eventType: 'marked_understood',
    icon: FiIcons.FiCheckCircle,
    compactLabel: 'I understand',
    label: 'I understand this',
    activeCompactLabel: 'Understood',
    activeLabel: 'Marked understood'
  },
  {
    feedbackType: 'confusing',
    eventType: 'marked_confusing',
    icon: FiIcons.FiAlertCircle,
    compactLabel: 'Confusing',
    label: 'This is confusing',
    activeCompactLabel: 'Confusing',
    activeLabel: 'Marked confusing',
    showInCompact: false
  },
  {
    feedbackType: 'ask_advisor',
    eventType: 'advisor_discussion_requested',
    icon: FiIcons.FiMessageCircle,
    compactLabel: 'Ask my advisor',
    label: 'Discuss with advisor',
    activeCompactLabel: 'Advisor asked',
    activeLabel: 'Advisor question noted'
  },
  {
    feedbackType: 'ask_attorney',
    eventType: 'attorney_discussion_requested',
    icon: FiIcons.FiMessageSquare,
    compactLabel: 'Ask attorney',
    label: 'Discuss with attorney',
    activeCompactLabel: 'Attorney asked',
    activeLabel: 'Attorney question noted'
  },
  {
    feedbackType: 'saved',
    eventType: 'topic_bookmarked',
    icon: FiIcons.FiBookmark,
    compactLabel: 'Save',
    label: 'Save topic',
    activeCompactLabel: 'Saved',
    activeLabel: 'Saved',
    showInCompact: false
  }
];

const LearningFeedbackButtons = ({
  topic,
  matterId,
  questionKey,
  userId,
  source,
  compact = false,
  className = ''
}) => {
  const [savingFeedback, setSavingFeedback] = useState(null);
  const [activeFeedbackTypes, setActiveFeedbackTypes] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const loadFeedbackState = async () => {
      if (!topic?.id || !userId) {
        setActiveFeedbackTypes([]);
        return;
      }

      try {
        const feedbackTypes = await fetchTopicFeedbackTypes({
          userId,
          matterId,
          topicId: topic.id
        });
        if (!cancelled) setActiveFeedbackTypes(feedbackTypes);
      } catch (err) {
        console.warn('Unable to load topic feedback:', err);
      }
    };

    loadFeedbackState();
    return () => {
      cancelled = true;
    };
  }, [topic?.id, userId, matterId]);

  const activeFeedbackSet = useMemo(() => new Set(activeFeedbackTypes), [activeFeedbackTypes]);

  const saveFeedback = async (action) => {
    if (!topic?.id || !userId || savingFeedback) return;
    const isActive = activeFeedbackSet.has(action.feedbackType);

    try {
      setSavingFeedback(action.feedbackType);
      if (isActive) {
        await removeTopicFeedback({
          userId,
          matterId,
          topic,
          feedbackType: action.feedbackType
        });
        setActiveFeedbackTypes((current) => current.filter((type) => type !== action.feedbackType));
      } else {
        await recordTopicFeedback({
          userId,
          matterId,
          questionKey,
          topic,
          feedbackType: action.feedbackType,
          eventType: action.eventType,
          source
        });
        setActiveFeedbackTypes((current) => Array.from(new Set([...current, action.feedbackType])));
      }
    } catch (err) {
      console.warn('Unable to save topic feedback:', err);
    } finally {
      setSavingFeedback(null);
    }
  };

  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      {feedbackActions.filter((action) => !compact || action.showInCompact !== false).map((action) => {
          const isActive = activeFeedbackSet.has(action.feedbackType);
          const label = isActive
            ? (compact ? action.activeCompactLabel : action.activeLabel)
            : (compact ? action.compactLabel : action.label);

          return (
            <button
              key={action.feedbackType}
              type="button"
              onClick={() => saveFeedback(action)}
              disabled={savingFeedback === action.feedbackType}
              title={isActive ? 'Click again to unmark this' : undefined}
              className={`inline-flex items-center rounded-md border px-3 py-1.5 text-xs font-bold uppercase tracking-widest disabled:opacity-75 ${
                compact ? '' : 'md:px-4 md:py-2'
              } ${isActive ? activeButtonStyles[action.feedbackType] : buttonStyles[action.feedbackType]}`}
            >
              <SafeIcon icon={isActive ? FiIcons.FiCheckCircle : action.icon} className={`${compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} mr-1.5`} />
              {label}
            </button>
          );
        })}
    </div>
  );
};

export default LearningFeedbackButtons;
